import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlay,
  faPause,
  faBackwardStep,
  faForwardStep,
  faShuffle,
  faRepeat,
  faVolumeUp,
  faVolumeDown,
} from '@fortawesome/free-solid-svg-icons'
import './MusicWindow.css'
import musicFile from '../../../assets/music/hoshi-to-bokura-to.mp3'
import albumArt from '../../../assets/music/hoshitobokurato.jpeg'
import closeIcon from '../../../assets/icons/close-icon.svg'
import minimizeIcon from '../../../assets/icons/collapse-icon.svg'
import { useOrientation } from '../../../contexts/OrientationContext'

function MusicWindow({ onClose }) {
  const { isPortrait } = useOrientation()
  const [isMinimized, setIsMinimized] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(70)
  const [isShuffle, setIsShuffle] = useState(false)
  const [repeatMode, setRepeatMode] = useState('off') // 'off', 'all', 'one'
  const audioRef = useRef(null)

  const musicUrl = musicFile // 使用导入的音频文件

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }



  const handleVolumeChange = e => {
    const newVolume = e.target.value
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100
    }
  }

  const handleShuffle = () => {
    setIsShuffle(!isShuffle)
  }

  const handleRepeat = () => {
    const modes = ['off', 'all', 'one']
    const currentIndex = modes.indexOf(repeatMode)
    const nextIndex = (currentIndex + 1) % modes.length
    setRepeatMode(modes[nextIndex])
  }

  const handlePrevious = () => {
    // 上一首功能（当前只有一首，可以重置到开头）
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const handleNext = () => {
    // 下一首功能（当前只有一首，可以重置到开头）
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100
    }
  }, [volume])

  // 最小化时，如果音乐正在播放，则继续播放
  React.useEffect(() => {
    if (isMinimized && isPlaying && audioRef.current) {
      // 确保音乐继续播放
      if (audioRef.current.paused) {
        audioRef.current.play().catch(err => {
          console.log('最小化时播放失败:', err)
        })
      }
    } else if (isMinimized && !isPlaying && audioRef.current) {
      // 如果用户没有播放音乐，最小化时暂停
      audioRef.current.pause()
    }
  }, [isMinimized, isPlaying])

  return (
    <div
      className={`music-window-overlay ${isMinimized ? 'minimized' : ''} ${isPortrait ? 'portrait' : ''}`}
    >
      {/* audio元素始终存在，确保音乐连续播放 */}
          <audio
            ref={audioRef}
            src={musicUrl}
            loop
          />
      <div className={`music-window ${isMinimized ? 'minimized' : ''}`}>
        {!isMinimized && (
          <>
            <div className="music-window-header">
              <div className="music-window-buttons">
                <img
                  className="close-icon"
                  onClick={onClose}
                  src={closeIcon}
                  alt="Close"
                  style={{ cursor: 'pointer', width: '12px', height: '12px' }}
                />
                <img
                  className="close-icon"
                  onClick={toggleMinimize}
                  src={minimizeIcon}
                  alt="Minimize"
                  style={{ cursor: 'pointer', width: '12px', height: '12px' }}
                />
              </div>
            </div>
            <div className="music-player">
              <div className="music-player-container">
                {/* 左侧播放控制 */}
                <div className="music-controls-left">
                  <button
                    className={`control-button shuffle-button ${isShuffle ? 'active' : ''}`}
                    onClick={handleShuffle}
                    title="随机播放"
                  >
                    <FontAwesomeIcon icon={faShuffle} />
                  </button>
                  <button
                    className="control-button previous-button"
                    onClick={handlePrevious}
                    title="上一首"
                  >
                    <FontAwesomeIcon icon={faBackwardStep} />
                  </button>
                  <button
                    className="control-button play-pause-button"
                    onClick={handlePlayPause}
                    title={isPlaying ? '暂停' : '播放'}
                  >
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                  </button>
                  <button
                    className="control-button next-button"
                    onClick={handleNext}
                    title="下一首"
                  >
                    <FontAwesomeIcon icon={faForwardStep} />
                  </button>
                  <button
                    className={`control-button repeat-button ${repeatMode !== 'off' ? 'active' : ''}`}
                    onClick={handleRepeat}
                    title={
                      repeatMode === 'off'
                        ? '重复'
                        : repeatMode === 'all'
                          ? '重复全部'
                          : '重复一首'
                    }
                  >
                    <FontAwesomeIcon icon={faRepeat} />
                  </button>
                </div>

                {/* 中间显示区域 */}
                <div className="music-display-area">
                  <div className={`music-album-art ${isPlaying ? 'playing' : ''}`}>
                    <img src={albumArt} alt="Album Art" />
                    <div className="cd-center">
                      <div className="cd-center-hole"></div>
                    </div>
                  </div>
                </div>

                {/* 右侧音量和其他控制 */}
                <div className="music-controls-right">
                  <div className="volume-control">
                    <FontAwesomeIcon icon={faVolumeDown} className="volume-icon" />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="volume-slider"
                    />
                    <FontAwesomeIcon icon={faVolumeUp} className="volume-icon" />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {isMinimized && (
          <div className="minimized-cd-container" onClick={toggleMinimize}>
            <div className={`music-album-art ${isPlaying ? 'playing' : ''}`}>
              <img src={albumArt} alt="Album Art" />
              <div className="cd-center">
                <div className="cd-center-hole"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

MusicWindow.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default MusicWindow
